import {
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  DialogActions,
  Button,
  Divider,
  Alert,
  Switch,
  FormControlLabel,
  Stack,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Check } from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useCustomMutation, useInvalidate } from "@refinedev/core";
import { useTranslation } from "../../../../hooks/useTranslation";
import { ShippingLabelModal } from "./ShippingLabelModal";
import { OrderDetail } from "../../types";

/* ======================================================
   CONFIG
====================================================== */
const USE_MOCK = false;

/* ======================================================
   TYPES – BACKEND RESPONSE (BUSINESS ERROR AWARE)
====================================================== */

export type CargoRateItem = {
  id: string;
  shipmentId: string;
  carrier: string;
  service: string;
  rate: number;
  currency: string;
};

export type CargoProviderBlock = {
  cargoProvider: string;
  result?: CargoRateItem[];
  errorMessage?: string[];
};

type ShipmentResponse = {
  trackingNumber: string;
  labelUrl: string;
  carrierName: string;
};

type CargoRatesStepProps = {
  orderDetails: OrderDetail;
  orderBarcode: string;
  itemPrices: { id: number; value: number; quantity: number }[];
  dimensions: {
    length: number;
    width: number;
    height: number;
    weight: number;
  };
  items: {
    productCode: string;
    quantity: number;
    orderItemId: number;
  }[];
  onBack: () => void;
  onClose: () => void;
};

/* ======================================================
   COMPONENT
====================================================== */

export const CargoRatesStep = ({
  orderDetails,
  orderBarcode,
  itemPrices,
  dimensions,
  items,
  onBack,
  onClose,
}: CargoRatesStepProps) => {
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.orderDetails.prepareShipment",
  );

  const invalidate = useInvalidate();

  /* ======================================================
     STATE
  ====================================================== */

  const [bypassAddress, setBypassAddress] = useState(false);

  const [selectedOption, setSelectedOption] = useState<CargoRateItem | null>(
    null,
  );
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);

  const [labelData, setLabelData] = useState<ShipmentResponse | null>(null);
  const [showLabelModal, setShowLabelModal] = useState(false);

  /* ======================================================
     FETCH CARGO RATES
  ====================================================== */

  const bypassItemsNotReceived = useMemo(() => {
    return items.some((shipmentItem) => {
      const orderItem = orderDetails.orderItems.find(
        (oi) => oi.id === shipmentItem.orderItemId,
      );

      if (!orderItem) return false;

      return shipmentItem.quantity > orderItem.receivedQuantity;
    });
  }, [items, orderDetails.orderItems]);

  const {
    mutate: fetchCargoRates,
    data: cargoRatesResponse,
    isLoading: cargoRatesLoading,
  } = useCustomMutation<CargoProviderBlock[]>();

  useEffect(() => {
    if (USE_MOCK) return;

    fetchCargoRates({
      url:
        `orders/${orderBarcode}/cargo-rates` +
        `?forceIfAddressInvalid=${bypassAddress}` +
        `&forceIfItemsNotReceived=${bypassItemsNotReceived}`,
      method: "put",
      values: {
        itemPrices,
        dimensions,
      },
    });
  }, [
    orderBarcode,
    bypassAddress,
    bypassItemsNotReceived,
    itemPrices,
    dimensions,
    fetchCargoRates,
  ]);

  const isLoading = USE_MOCK ? false : cargoRatesLoading;

  /* ======================================================
     CONFIRM SHIPMENT
  ====================================================== */

  const { mutate: confirmShipment, isLoading: confirmLoading } =
    useCustomMutation<ShipmentResponse>();

  const handleConfirmShipment = () => {
    if (!selectedOption || !selectedProvider) return;

    confirmShipment(
      {
        url: `orders/${orderBarcode}/shipment?forceIfItemsNotReceived=${bypassItemsNotReceived}`,
        method: "post",
        values: {
          provider: selectedProvider,
          shipmentId: selectedOption.shipmentId,
          rateId: selectedOption.id,
          shipmentRequest: {
            itemPrices,
            dimensions,
          },
          items,
        },
        successNotification: () => ({
          message: translate("successMessage"),
          type: "success",
        }),
      },
      {
        onSuccess: (response) => {
          if (response.data) {
            setLabelData(response.data);
            setShowLabelModal(true);
          }
          invalidate({ resource: "all", invalidates: ["all"] });
        },
      },
    );
  };

  /* ======================================================
     DERIVED STATE
  ====================================================== */

  const providers = cargoRatesResponse?.data ?? [];

  const hasAnyProviderError = useMemo(
    () => providers.some((p) => p.errorMessage && p.errorMessage.length > 0),
    [providers],
  );

  /* ======================================================
     RENDER HELPERS
  ====================================================== */

  const renderProviderOptions = (provider: CargoProviderBlock) => {
    if (provider.errorMessage && provider.errorMessage.length > 0) {
      return (
        <Alert severity="error" sx={{ mb: 1 }}>
          <Typography fontWeight={700}>{provider.cargoProvider}</Typography>
          <ul style={{ margin: "6px 0 0 16px" }}>
            {provider.errorMessage.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        </Alert>
      );
    }

    if (!provider.result || provider.result.length === 0) {
      return (
        <Alert severity="warning">
          {translate("noCarrierOptionsAvailable")}
        </Alert>
      );
    }

    // 🔽 FİYATA GÖRE ARTAN SIRALAMA
    const sortedRates = [...provider.result].sort((a, b) => a.rate - b.rate);

    return sortedRates.map((option) => {
      const isSelected = selectedOption?.id === option.id;

      return (
        <ListItem
          key={option.id}
          disablePadding
          sx={{
            border: "1px solid",
            borderColor: isSelected ? "primary.main" : "divider",
            borderRadius: 1,
            mb: 1,
          }}
        >
          <ListItemButton
            onClick={() => {
              setSelectedOption(option);
              setSelectedProvider(provider.cargoProvider);
            }}
          >
            <ListItemIcon>
              <Radio checked={isSelected} />
            </ListItemIcon>

            <ListItemText
              primary={
                <Typography fontWeight={600}>{option.carrier}</Typography>
              }
              secondary={option.service}
            />

            <Typography fontWeight={700}>
              {option.rate.toFixed(2)} {option.currency}
            </Typography>
          </ListItemButton>
        </ListItem>
      );
    });
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (providers.length === 0) {
      return (
        <Typography align="center" color="text.secondary">
          {translate("noCarrierOptionsAvailable")}
        </Typography>
      );
    }

    return (
      <List sx={{ maxHeight: 420, overflow: "auto" }}>
        {providers.map((provider) => (
          <Box key={provider.cargoProvider} mb={2}>
            <Typography variant="subtitle2" fontWeight={700} color="primary">
              {provider.cargoProvider}
            </Typography>

            <Divider sx={{ mb: 1 }} />

            {renderProviderOptions(provider)}
          </Box>
        ))}
      </List>
    );
  };

  /* ======================================================
     UI
  ====================================================== */

  return (
    <>
      <DialogContent>{renderContent()}</DialogContent>

      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Stack spacing={1} flex={1}>
          <FormControlLabel
            control={
              <Switch
                checked={bypassAddress}
                onChange={() => setBypassAddress((p) => !p)}
                color={hasAnyProviderError ? "warning" : "primary"}
              />
            }
            label={translate("bypassValidation")}
          />

          {/*  <FormControlLabel
            control={
              <Switch
                checked={bypassItemsNotReceived}
                onChange={() => setBypassItemsNotReceived((p) => !p)}
              />
            }
            label={translate("bypassItemsNotReceived")}
          /> */}
        </Stack>

        <Button onClick={onBack}>
          {translateWithoutPrefix("buttons.back")}
        </Button>

        <LoadingButton
          variant="contained"
          loading={confirmLoading}
          disabled={!selectedOption}
          startIcon={<Check />}
          onClick={handleConfirmShipment}
        >
          {translate("confirmShipment")}
        </LoadingButton>
      </DialogActions>

      <ShippingLabelModal
        open={showLabelModal}
        onClose={onClose}
        trackingNumber={labelData?.trackingNumber}
        labelUrl={labelData?.labelUrl}
        carrierName={labelData?.carrierName}
      />
    </>
  );
};
